import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { SEOArticle } from '@/components/base/SEOSchema';
import { comics, categories } from '@/mocks/comics';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  const comic = useMemo(() => comics.find((c) => c.id === id), [id]);
  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics.filter((c) => c.id !== comic.id && c.category === comic.category).slice(0, 4);
  }, [comic]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要找的漫画不存在或已下架</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors">
              <i className="ri-home-4-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryName = categories.find((c) => c.slug === comic.category)?.name || comic.category;

  return (
    <>
      <SEOArticle
        title={`${comic.title}韩漫-${comic.title}全集免费阅读-51漫画网`}
        description={`${comic.title}韩漫全集免费在线阅读。${comic.description.slice(0, 100)}。作者${comic.author}，共${comic.totalChapters}话，${comic.status}，评分${comic.rating}分。`}
        url={`https://ytzshn.cn/article/${comic.id}`}
        author={comic.author}
        datePublished="2025-01-15"
        dateModified={comic.updatedAt}
        imageUrl={comic.cover}
        breadcrumbItems={[
          { name: '首页', url: 'https://ytzshn.cn' },
          { name: `${categoryName}韩漫`, url: `https://ytzshn.cn/category/${comic.category}` },
          { name: comic.title, url: `https://ytzshn.cn/article/${comic.id}` },
        ]}
      />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: `${categoryName}韩漫`, href: `/category/${comic.category}` },
            { label: comic.title },
          ]} />

          {/* Comic Detail Header */}
          <article className="flex flex-col md:flex-row gap-8 py-8">
            <div className="flex-shrink-0">
              <div className="w-[200px] h-[270px] md:w-[240px] md:h-[320px] rounded-xl overflow-hidden">
                <img
                  src={comic.cover}
                  alt={`${comic.title} 封面`}
                  title={`${comic.title} - 韩漫全集免费阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${comic.status === '连载中' ? 'bg-accent-500 text-background-50' : 'bg-foreground-600 text-background-50'}`}>
                  {comic.status}
                </span>
                {comic.tags.map((tag) => (
                  <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-background-100 text-foreground-600">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-2">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">作者：{comic.author}</p>

              <div className="flex items-center gap-4 mt-4 flex-wrap">
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-amber-400"></i>
                  <span className="text-sm font-semibold text-foreground-900">{comic.rating}</span>
                  <span className="text-xs text-foreground-500">分</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-eye-line text-foreground-400"></i>
                  <span className="text-sm text-foreground-600">{comic.views.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-heart-line text-foreground-400"></i>
                  <span className="text-sm text-foreground-600">{comic.favorites.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-pages-line text-foreground-400"></i>
                  <span className="text-sm text-foreground-600">共{comic.totalChapters}话</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-time-line text-foreground-400"></i>
                  <span className="text-sm text-foreground-600">更新于 {comic.updatedAt}</span>
                </div>
              </div>

              <p className="text-sm text-foreground-600 mt-5 leading-relaxed">
                {comic.description}
              </p>

              <div className="flex items-center gap-3 mt-6">
                <button className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2">
                  <i className="ri-play-circle-line"></i>
                  开始阅读 第1话
                </button>
                <button className="px-6 py-3 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2">
                  <i className="ri-bookmark-line"></i>
                  加入收藏
                </button>
              </div>
            </div>
          </article>

          {/* Chapter List */}
          <section className="pb-8" aria-label="章节列表">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-lg font-bold text-foreground-900">章节列表</h2>
              <div className="flex items-center gap-2">
                <button className="text-xs text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer">正序</button>
                <span className="text-xs text-foreground-300">|</span>
                <button className="text-xs text-primary-600 font-medium cursor-pointer">倒序</button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {comic.chapters.map((ch) => (
                <button
                  key={ch.id}
                  className="text-left px-4 py-3 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer group"
                >
                  <p className="text-sm text-foreground-700 group-hover:text-primary-600 line-clamp-1 transition-colors">
                    {ch.title}
                  </p>
                  <p className="text-xs text-foreground-400 mt-1">{ch.updatedAt}</p>
                </button>
              ))}
            </div>
          </section>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section className="py-8 border-t border-background-200/70" aria-label="相关推荐">
              <h2 className="font-heading text-lg font-bold text-foreground-900 mb-5">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} size="small" />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}